// components/market/DetailProduit.tsx
//
// Composant RÉUTILISABLE affichant le détail complet d'un produit
// (description, prix, galerie d'images, boutons d'action). Ne fait
// aucun fetch lui-même — reçoit le produit déjà chargé en prop, pour
// être utilisable à la fois par :
//   - app/(tabs)/market/produits/[id].tsx (écran "Voir plus")
//   - app/(tabs)/market/boutiques/[slug].tsx (page boutique complète,
//     pour afficher le détail d'un produit de cette boutique sans
//     dupliquer toute cette UI)
//
// Les 4 mêmes actions que sur la carte du feed (index.tsx) sont
// reprises ici, chacune avec son propre TouchableOpacity indépendant :
// WhatsApp, Acheter, Contacter le vendeur (si pas de WhatsApp Business),
// Aller en boutique.
//
// PATCH V12 : product.name → product.product_name (voir useProduct.ts /
// useMarketFeed.ts, patch V12 et handleMarket.ts).
//
// PATCH V13 :
//   - Image de couverture + 2 miniatures (image_2_url, image_3_url)
//     remplacées par de vrais <Image>, avec repli sur le placeholder
//     Ionicons d'origine si l'URL est absente ou si le chargement
//     échoue (onError) — un état d'échec par image, indépendant.
//   - Ajout de l'affichage de description_short (résumé), distinct de
//     description (texte long) — absent jusqu'ici alors que le champ
//     existe dans MarketProductDetail. Affiché juste sous le nom/
//     boutique, avant le bloc prix, comme accroche courte.
//
// PATCH V14 : ajout des boutons "En savoir plus" (show_learn_more_button
// / learn_more_url → Linking.openURL) et "Appeler" (show_call_button /
// call_phone → Linking.openURL('tel:...')), absents jusqu'ici alors que
// ces champs existent dans MarketProductDetail et sont bien remontés
// par le backend (voir handleMarket.ts, getProduct via select('*') sur
// market_marketplace_feed). Chacun a son propre TouchableOpacity
// indépendant, même pattern que WhatsApp/Acheter/Contacter le vendeur —
// aucun champ n'est laissé de côté volontairement.
//
// PATCH V15 : seo_title / seo_description / og_image_url n'avaient
// aucun usage jusqu'ici. Ajout de balises <Head> (expo-router/head) —
// web only, ignorées silencieusement sur natif — pour le titre de
// l'onglet/page et les balises meta description + Open Graph (title,
// description, image, type=product). Fallback sur product_name /
// description_short / cover_image_url quand les champs SEO dédiés sont
// vides, pour ne jamais publier une balise vide côté web.
//
// PATCH V17 : installation du système AutoScroll + double tap, repris
// de app/(tabs)/market/index.tsx (mêmes hooks : useScrollAutoHide +
// useTabBarVisibility). Contrairement à index.tsx, ce composant n'a
// aucun header interne à animer (translateY) — le double tap et le
// scroll pilotent donc UNIQUEMENT la visibilité de la tab bar globale,
// pas un Animated.View local. Un état local `tabBarHiddenRef` (via
// useRef, pas useState) suit l'état actuel côté composant pour permettre
// le toggle au double tap, tout en appelant setTabBarHidden avec un
// booléen explicite (true/false) — jamais de fonction updater — pour
// rester compatible avec la signature utilisée dans index.tsx
// (setTabBarHidden(true) / setTabBarHidden(false)).
// Installé ici uniquement (dans le composant partagé), donc actif
// automatiquement sur les deux écrans qui l'utilisent : produits/[id].tsx
// et boutiques/[slug].tsx — conformément au choix fait pour cette mise
// à jour.
//
// PATCH i18n : tous les textes affichés à l'utilisateur passent par
// react-i18next (namespace market.components.detailProduit /
// market.common). L'état (Neuf/Occasion/Reconditionné) réutilise
// market.common.conditions, partagé avec les écrans de gestion.

import React, { useCallback, useRef, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  Linking,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useTranslation } from 'react-i18next';

import type { MarketProductDetail } from '@/hooks/market/useProduct';
import { useContactSeller } from '@/hooks/market/useContactSeller';
import { useFavorites } from '@/hooks/market/useFavorites';
import { useScrollAutoHide } from '@/hooks/useScrollAutoHide';
import { useTabBarVisibility } from '@/components/CustomTabBar';

const NATIVE = Platform.OS !== 'web';

function formatPrice(amount: number, currency: string) {
  return `${amount.toLocaleString('fr-FR')} ${currency}`;
}

type DetailProduitProps = {
  product: MarketProductDetail;
};

export default function DetailProduit({ product }: DetailProduitProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const { contactSeller, loading: contactLoading } = useContactSeller();
  const { isFavorite, toggleFavorite, togglingId: togglingFavoriteId } = useFavorites();

  // ── AutoScroll + double tap (PATCH V17) ──
  // Pas de header interne ici : on pilote uniquement setTabBarHidden,
  // toujours avec un booléen explicite (jamais d'updater), pour rester
  // cohérent avec l'usage fait dans index.tsx. tabBarHiddenRef suit
  // l'état actuel côté composant (via useRef, pas useState — évite un
  // re-render inutile à chaque scroll) pour permettre le toggle au
  // double tap.
  const { setTabBarHidden } = useTabBarVisibility();
  const tabBarHiddenRef = useRef(false);
  const [lastTap, setLastTap] = useState<number | null>(null);

  const showTabBar = useCallback(() => {
    if (tabBarHiddenRef.current) {
      tabBarHiddenRef.current = false;
      setTabBarHidden(false);
    }
  }, [setTabBarHidden]);

  const hideTabBar = useCallback(() => {
    if (!tabBarHiddenRef.current) {
      tabBarHiddenRef.current = true;
      setTabBarHidden(true);
    }
  }, [setTabBarHidden]);

  const { handleScroll } = useScrollAutoHide({
    onShow: showTabBar,
    onHide: hideTabBar,
  });

  const toggleTabBar = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (tabBarHiddenRef.current) showTabBar();
    else hideTabBar();
  }, [showTabBar, hideTabBar]);

  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (lastTap && now - lastTap < 300) toggleTabBar();
    else setLastTap(now);
  }, [lastTap, toggleTabBar]);

  // Un état d'échec par image — indépendants les uns des autres, pour
  // qu'une miniature cassée ne fasse pas basculer les autres en
  // placeholder.
  const [coverFailed, setCoverFailed] = useState(false);
  const [thumb2Failed, setThumb2Failed] = useState(false);
  const [thumb3Failed, setThumb3Failed] = useState(false);

  const showCover  = !!product.cover_image_url && !coverFailed;
  const showThumb2 = !!product.image_2_url && !thumb2Failed;
  const showThumb3 = !!product.image_3_url && !thumb3Failed;

  const handleToggleFavorite = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    toggleFavorite(product.product_id);
  }, [product.product_id, toggleFavorite]);

  const handleWhatsApp = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const phone = product.whatsapp_business_phone_override;
    if (!phone) return;
    const cleanPhone = phone.replace(/[^\d]/g, '');
    const message = encodeURIComponent(t('market.components.detailProduit.whatsappMessage', { product: product.product_name }));
    Linking.openURL(`https://wa.me/${cleanPhone}?text=${message}`);
  }, [product, t]);

  const handleBuy = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.buy_url) return;
    Linking.openURL(product.buy_url);
  }, [product]);

  const handleLearnMore = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.learn_more_url) return;
    Linking.openURL(product.learn_more_url);
  }, [product]);

  const handleCall = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    if (!product.call_phone) return;
    Linking.openURL(`tel:${product.call_phone}`);
  }, [product]);

  const handleContactSeller = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    contactSeller({
      id:     product.owner_id,
      nom:    product.owner_nom,
      prenom: product.owner_prenom,
    });
  }, [product, contactSeller]);

  const handleGoToShop = useCallback(() => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.push(`/(tabs)/market/boutiques/${product.shop_slug}`);
  }, [router, product.shop_slug]);

  // WhatsApp Business prioritaire ; sinon bouton messagerie interne si
  // configuré (show_platform_chat_button).
  const showWhatsApp   = product.show_whatsapp_business_button;
  const showContactSeller = !showWhatsApp && product.show_platform_chat_button;

  // Fallback : jamais de balise vide côté web si les champs SEO dédiés
  // ne sont pas renseignés — on retombe sur les champs de contenu
  // existants.
  const metaTitle = product.seo_title?.trim() || product.product_name;
  const metaDescription = product.seo_description?.trim() || product.description_short?.trim() || undefined;
  const metaImage = product.og_image_url?.trim() || product.cover_image_url || undefined;

  const conditionLabel = product.condition === 'new'
    ? t('market.common.conditions.new')
    : product.condition === 'used'
      ? t('market.common.conditions.used')
      : t('market.common.conditions.refurbished');

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {!!metaDescription && <meta name="description" content={metaDescription} />}
        <meta property="og:title" content={metaTitle} />
        {!!metaDescription && <meta property="og:description" content={metaDescription} />}
        {!!metaImage && <meta property="og:image" content={metaImage} />}
        <meta property="og:type" content="product" />
      </Head>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
      {/* ── Galerie (image de couverture + jusqu'à 2 images secondaires) ── */}
      {showCover ? (
        <View style={styles.imageWrap}>
          <Image
            source={{ uri: product.cover_image_url }}
            style={styles.image}
            resizeMode="cover"
            onError={() => setCoverFailed(true)}
          />
          {product.is_featured && (
            <View style={styles.featuredBadge}>
              <Text style={styles.featuredBadgeText}>{t('market.components.detailProduit.newBadge')}</Text>
            </View>
          )}
          <TouchableOpacity
            style={styles.favoriteBtn}
            activeOpacity={0.7}
            onPress={handleToggleFavorite}
            disabled={togglingFavoriteId === product.product_id}
          >
            <Ionicons
              name={isFavorite(product.product_id) ? 'heart' : 'heart-outline'}
              size={20}
              color={isFavorite(product.product_id) ? '#EF4444' : '#999'}
            />
          </TouchableOpacity>
        </View>
      ) : (
        <View style={styles.imagePlaceholder}>
          <Ionicons name="image-outline" size={48} color="#C4B5FD" />
          {product.is_featured && (
            <View style={styles.featuredBadge}>
              <Text style={styles.featuredBadgeText}>{t('market.components.detailProduit.newBadge')}</Text>
            </View>
          )}
          <TouchableOpacity
            style={styles.favoriteBtn}
            activeOpacity={0.7}
            onPress={handleToggleFavorite}
            disabled={togglingFavoriteId === product.product_id}
          >
            <Ionicons
              name={isFavorite(product.product_id) ? 'heart' : 'heart-outline'}
              size={20}
              color={isFavorite(product.product_id) ? '#EF4444' : '#999'}
            />
          </TouchableOpacity>
        </View>
      )}

      {(product.image_2_url || product.image_3_url) && (
        <View style={styles.thumbRow}>
          {product.image_2_url && (
            showThumb2 ? (
              <Image
                source={{ uri: product.image_2_url }}
                style={styles.thumb}
                resizeMode="cover"
                onError={() => setThumb2Failed(true)}
              />
            ) : (
              <View style={styles.thumbPlaceholder}>
                <Ionicons name="image-outline" size={22} color="#C4B5FD" />
              </View>
            )
          )}
          {product.image_3_url && (
            showThumb3 ? (
              <Image
                source={{ uri: product.image_3_url }}
                style={styles.thumb}
                resizeMode="cover"
                onError={() => setThumb3Failed(true)}
              />
            ) : (
              <View style={styles.thumbPlaceholder}>
                <Ionicons name="image-outline" size={22} color="#C4B5FD" />
              </View>
            )
          )}
        </View>
      )}

      {/* ── Nom, boutique, résumé, prix ── */}
      <Text style={styles.name}>{product.product_name}</Text>
      <TouchableOpacity onPress={handleGoToShop} activeOpacity={0.7}>
        <Text style={styles.shopName}>{product.shop_name}</Text>
      </TouchableOpacity>

      {!!product.description_short && (
        <Text style={styles.descriptionShort}>{product.description_short}</Text>
      )}

      <View style={styles.priceRow}>
        {product.promo_price ? (
          <>
            <Text style={styles.promoPrice}>{formatPrice(product.promo_price, product.currency_code)}</Text>
            <Text style={styles.originalPrice}>{formatPrice(product.price, product.currency_code)}</Text>
          </>
        ) : (
          <Text style={styles.price}>{formatPrice(product.price, product.currency_code)}</Text>
        )}
      </View>

      {product.stock_quantity > 0 ? (
        <View style={styles.stockRow}>
          <Ionicons name="checkmark-circle" size={14} color="#10B981" />
          <Text style={styles.stockTextAvailable}>
            {t('market.components.detailProduit.inStock', { count: product.stock_quantity })}
          </Text>
        </View>
      ) : (
        <View style={styles.stockRow}>
          <Ionicons name="close-circle" size={14} color="#EF4444" />
          <Text style={styles.stockTextUnavailable}>{t('market.components.detailProduit.outOfStock')}</Text>
        </View>
      )}

      {/* ── Description ── */}
      {!!product.description && (
        <View style={styles.descriptionBlock}>
          <Text style={styles.descriptionTitle}>{t('market.components.detailProduit.descriptionTitle')}</Text>
          <Text style={styles.descriptionText}>{product.description}</Text>
        </View>
      )}

      {/* ── Infos secondaires ── */}
      {(product.brand || product.condition) && (
        <View style={styles.metaRow}>
          {!!product.brand && (
            <View style={styles.metaChip}>
              <Text style={styles.metaChipText}>{product.brand}</Text>
            </View>
          )}
          <View style={styles.metaChip}>
            <Text style={styles.metaChipText}>
              {conditionLabel}
            </Text>
          </View>
        </View>
      )}

      {/* ── Boutons d'action ── */}
      <View style={styles.actionsBlock}>
        {showWhatsApp && (
          <TouchableOpacity style={styles.primaryBtn} activeOpacity={0.8} onPress={handleWhatsApp}>
            <Ionicons name="logo-whatsapp" size={18} color="#fff" />
            <Text style={styles.primaryBtnText}>{t('market.components.detailProduit.whatsapp')}</Text>
          </TouchableOpacity>
        )}

        {showContactSeller && (
          <TouchableOpacity
            style={styles.primaryBtn}
            activeOpacity={0.8}
            onPress={handleContactSeller}
            disabled={contactLoading}
          >
            <Ionicons name="chatbubble-ellipses-outline" size={18} color="#fff" />
            <Text style={styles.primaryBtnText}>
              {contactLoading ? t('market.components.detailProduit.contacting') : t('market.components.detailProduit.contactSeller')}
            </Text>
          </TouchableOpacity>
        )}

        {product.show_buy_button && (
          <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleBuy}>
            <Ionicons name="cart-outline" size={18} color="#6610B1" />
            <Text style={styles.secondaryBtnText}>{t('market.components.detailProduit.buy')}</Text>
          </TouchableOpacity>
        )}

        {product.show_learn_more_button && (
          <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleLearnMore}>
            <Ionicons name="information-circle-outline" size={18} color="#6610B1" />
            <Text style={styles.secondaryBtnText}>{t('market.components.detailProduit.learnMore')}</Text>
          </TouchableOpacity>
        )}

        {product.show_call_button && (
          <TouchableOpacity style={styles.secondaryBtn} activeOpacity={0.8} onPress={handleCall}>
            <Ionicons name="call-outline" size={18} color="#6610B1" />
            <Text style={styles.secondaryBtnText}>{t('market.components.detailProduit.call')}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity style={styles.tertiaryBtn} activeOpacity={0.8} onPress={handleGoToShop}>
          <Ionicons name="storefront-outline" size={18} color="#6610B1" />
          <Text style={styles.tertiaryBtnText}>{t('market.components.detailProduit.goToShop')}</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  imageWrap: {
    width: '100%',
    height: 240,
    borderRadius: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F3E8FF',
  },
  imagePlaceholder: {
    width: '100%',
    height: 240,
    borderRadius: 16,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: '#6610B1',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  featuredBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
  },
  favoriteBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  thumb: {
    width: 60,
    height: 60,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
  },
  thumbPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 10,
    backgroundColor: '#F3E8FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  name: {
    fontSize: 20,
    fontWeight: '800',
    color: '#222',
    marginTop: 18,
  },
  shopName: {
    fontSize: 14,
    color: '#6610B1',
    fontWeight: '600',
    marginTop: 4,
  },
  descriptionShort: {
    fontSize: 13.5,
    color: '#666',
    marginTop: 8,
    lineHeight: 19,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 8,
    marginTop: 12,
  },
  price: {
    fontSize: 22,
    fontWeight: '800',
    color: '#6610B1',
  },
  promoPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: '#6610B1',
  },
  originalPrice: {
    fontSize: 15,
    color: '#999',
    textDecorationLine: 'line-through',
  },
  stockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 8,
  },
  stockTextAvailable: {
    fontSize: 12.5,
    color: '#10B981',
    fontWeight: '600',
  },
  stockTextUnavailable: {
    fontSize: 12.5,
    color: '#EF4444',
    fontWeight: '600',
  },
  descriptionBlock: {
    marginTop: 22,
  },
  descriptionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  descriptionText: {
    fontSize: 14,
    color: '#444',
    lineHeight: 21,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 16,
  },
  metaChip: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
  },
  metaChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
  },
  actionsBlock: {
    marginTop: 26,
    gap: 10,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#25D366',
    paddingVertical: 14,
    borderRadius: 12,
  },
  primaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#fff',
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#F3E8FF',
    paddingVertical: 14,
    borderRadius: 12,
  },
  secondaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#6610B1',
  },
  tertiaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: '#6610B1',
    paddingVertical: 14,
    borderRadius: 12,
  },
  tertiaryBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#6610B1',
  },
});
